import { useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { openSupport, refreshSupportSummary, useSupportSummary } from "./supportStore";
import "./support.css";

// Пункты «Администратор» и «Поддержка» в низу бокового меню, над блоком
// пользователя (эталон: .k-adm, .k-sup). У каждого — синяя точка с числом:
// непрочитанные ответы поддержки и, у администратора, обращения, ждущие
// ответа. Нажатие открывает окно (SupportHost в AppShell), а не страницу, —
// поэтому это кнопки, а не ссылки меню.

/** Как часто спрашивать числа, пока окно закрыто. Ответ поддержки — не звонок,
 *  минута не мешает; а запрос копеечный (две суммы по индексу). */
export const SUPPORT_POLL_MS = 60_000;

/** Число в точке: сотня и больше — «99+», чтобы точка не растягивалась. */
export function dotText(n: number): string {
  return n > 99 ? "99+" : String(n);
}

function Glyph() {
  // Вопрос в кружке — тот же значок у обоих пунктов, как в эталоне.
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="7.2" />
      <path d="M7.9 8c0-1.3 1-2.1 2.2-2.1 1.2 0 2.1.8 2.1 1.9 0 1.5-2.2 1.6-2.2 3.2M10 13.9h.01" />
    </svg>
  );
}

interface TipHandlers {
  collapsed: boolean;
  showTip: (el: HTMLElement, text: string) => void;
  hideTip: () => void;
}

function Item({
  label,
  hint,
  count,
  countLabel,
  onClick,
  tip,
}: {
  label: string;
  hint: string;
  count: number;
  countLabel: string;
  onClick: () => void;
  tip: TipHandlers;
}) {
  return (
    <button
      type="button"
      className="nav-item nav-item--button"
      title={tip.collapsed ? undefined : hint}
      aria-label={count > 0 ? `${label}, ${countLabel}: ${count}` : label}
      onMouseEnter={(e) => tip.showTip(e.currentTarget, label)}
      onMouseLeave={tip.hideTip}
      onFocus={(e) => tip.showTip(e.currentTarget, label)}
      onBlur={tip.hideTip}
      onClick={() => {
        tip.hideTip();
        onClick();
      }}
    >
      <span className="nav-item-icon sup-ic">
        <Glyph />
        {count > 0 && <b className="sup-dot">{dotText(count)}</b>}
      </span>
      <span className="nav-item-label">{label}</span>
    </button>
  );
}

export function SupportMenu(tip: TipHandlers) {
  const { state } = useAuth();
  const user = state.status === "authed" ? state.user : null;
  const isAdmin = user?.role === "sysadmin";
  const { unread, waiting } = useSupportSummary();

  useEffect(() => {
    if (!user) return;
    void refreshSupportSummary();
    const id = window.setInterval(() => void refreshSupportSummary(), SUPPORT_POLL_MS);
    // Вернулись на вкладку — не ждать минуты, показать свежее.
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshSupportSummary();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.id]);

  return (
    <div className="sidebar-support">
      {isAdmin && (
        <Item
          label="Администратор"
          hint="Администратор: обращения пользователей"
          count={waiting}
          countLabel="ждут ответа"
          onClick={() => openSupport("admin")}
          tip={tip}
        />
      )}
      <Item
        label="Поддержка"
        hint="Поддержка"
        count={unread}
        countLabel="непрочитанных"
        onClick={() => openSupport("user")}
        tip={tip}
      />
    </div>
  );
}
